import { Close as CloseIcon } from 'components/icons';
import Checkmark from 'assets/images/icons/CheckmarkIcon.svg?react';
import StatusChip, { ChipType } from 'components/@extended/StatusChip.tsx';
import Loader from 'components/Loader.tsx';
import React from 'react';
import {
    CheckStatusType,
    PartyComplianceCheckMinDto,
    RiskProfileType,
    VerificationStatusType,
} from 'types/compliance.ts';
import { getClassifierText } from 'utils/getTranslations.ts';
import { useClassifiers } from 'utils/useClassifiers.ts';

export const getVerificationStatusChipType = (status: VerificationStatusType | null | undefined): ChipType => {
    switch (status) {
        case VerificationStatusType.VERIFIED:
            return 'success';
        case VerificationStatusType.NOT_VERIFIED:
        case VerificationStatusType.EXPIRED:
            return 'error';
        default:
            return 'warning';
    }
};

export const getRiskProfileStatusChipType = (status: RiskProfileType | null | undefined) => {
    switch (status) {
        case RiskProfileType.HIGH:
        case RiskProfileType.STOP_ONBOARDING:
            return 'error';
        case RiskProfileType.LOW:
            return 'success';
        default:
            return 'warning';
    }
};

export const getCheckStatusChipType = (status: CheckStatusType) => {
    switch (status) {
        case CheckStatusType.PASSED:
            return 'success';
        default:
            return 'error';
    }
};

interface VerificatoinStatusChipProps {
    status?: VerificationStatusType;
}

export const VerificatoinStatusChip: React.FC<VerificatoinStatusChipProps> = ({ status }) => {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['COMPLIANCE_VERIFICATION_STATUS_TYPE'],
    });

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    if (!status) {
        return null;
    }

    return (
        <StatusChip
            className="self-start text-xs"
            label={getClassifierText(classifiers['COMPLIANCE_VERIFICATION_STATUS_TYPE'], status)}
            type={getVerificationStatusChipType(status)}
        />
    );
};

const CheckStatusIcon: React.FC<Pick<PartyComplianceCheckMinDto, 'complianceCheckStatusTypeCode'>> = ({
    complianceCheckStatusTypeCode: status,
}) => {
    return status === CheckStatusType.PASSED ? (
        <span className="inline-flex size-6 items-center justify-center rounded-[4px]">
            <Checkmark className="text-success" width={14} height={14} />
        </span>
    ) : (
        <CloseIcon className="text-error" />
    );
};

interface CheckStatusProps {
    check: PartyComplianceCheckMinDto;
}

export const CheckStatus: React.FC<CheckStatusProps> = ({ check }) => {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: ['COMPLIANCE_CHECK_TYPE'] });

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <div className="flex">
            <CheckStatusIcon complianceCheckStatusTypeCode={check.complianceCheckStatusTypeCode} />
            <p className="text-body1">
                {check.isManuallyEntered
                    ? check.complianceCheckName
                    : getClassifierText(classifiers['COMPLIANCE_CHECK_TYPE'], check.complianceCheckTypeCode)}
            </p>
        </div>
    );
};
